import { useState, useEffect, useCallback } from 'react';
import { formatFranc } from '../lib/utils';
import api from '../lib/api';
import { teamAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, Flame, Star, Plus, BarChart3, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';
import EntityAvatar from '../components/EntityAvatar';

const TYPE_LABELS = {
  country_leader: 'Leader', ceo: 'CEO', actor: 'Actor',
  athlete: 'Athlete', musician: 'Artist', influencer: 'Influencer',
};

// v5.8.4 — page size that matches the backend's default and stays
// gentle on the trending pipeline (which sorts 1,500+ entities). Picked
// large enough that each Load More click feels meaningful but small
// enough that the first render is fast.
const PAGE_SIZE = 15;

function EntityRow({ entity, onDraft, isDrafted, showOwnership, locked }) {
  return (
    <div className="flex items-center gap-3 bg-card border border-border/60 rounded-xl px-3 py-2.5">
      <EntityAvatar entity={entity} size={36} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{entity.name}</p>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
          <span>{TYPE_LABELS[entity.entity_type] || entity.entity_type}</span>
          {entity.metadata?.country && <span>• {entity.metadata.country}</span>}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="text-right">
          <p className="text-sm font-bold tabular-nums">{formatFranc(entity.pricing?.price)}</p>
          {showOwnership && (
            <p className="text-[10px] text-muted-foreground flex items-center gap-0.5 justify-end">
              <Users className="h-2.5 w-2.5" />
              {entity.stats?.ownership_percentage?.toFixed(0) || 0}%
            </p>
          )}
          {entity.stats?.weekly_points > 0 && (
            <span className="badge-positive text-[9px] px-1.5 py-0.5 rounded-full">
              {formatFranc(entity.stats.weekly_points, { sign: true })}
            </span>
          )}
        </div>
        {!isDrafted && !locked && onDraft && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={(e) => { e.stopPropagation(); onDraft(entity); }}
            data-testid={`transfer-market-draft-${entity.id}`}
          >
            <Plus className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function TransferMarket({ onEntityClick, onBack, team: passedTeam, selectedLeagueId }) {
  // v5.8.4 — per-tab pagination state. Each tab keeps its own list and
  // hasMore flag so switching back and forth doesn't lose the user's
  // scroll position OR re-fetch already-loaded rows. The backend's
  // `/market/trending` endpoint accepts `skip` so each Load More click
  // requests the next page independently per column.
  const [tab, setTab] = useState('top_scorers');
  const [lists, setLists] = useState({ top_scorers: [], most_owned: [], rising_stars: [] });
  const [hasMore, setHasMore] = useState({ top_scorers: true, most_owned: true, rising_stars: true });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState({ top_scorers: false, most_owned: false, rising_stars: false });
  const [myTeam, setMyTeam] = useState(null);

  // Fetch a page; `skip=0` for initial load, `skip=lists[tab].length` for
  // subsequent Load More clicks. We always request all four columns at
  // once (the backend pipeline is already paying that cost) and append
  // each into the right tab.
  const fetchPage = useCallback(async (skip) => {
    const res = await api.get('/market/trending', { params: { limit: PAGE_SIZE, skip } });
    return res.data;
  }, []);

  useEffect(() => {
    Promise.all([
      fetchPage(0),
      teamAPI.myTeams().catch(() => ({ data: { teams: [] } })),
    ]).then(([market, teamRes]) => {
      setLists({
        top_scorers: market.top_scorers || [],
        most_owned: market.most_owned || [],
        rising_stars: market.rising_stars || [],
      });
      setHasMore({
        top_scorers: market.has_more?.top_scorers ?? true,
        most_owned: market.has_more?.most_owned ?? true,
        rising_stars: market.has_more?.rising_stars ?? true,
      });
      if (teamRes.data.teams?.length > 0) {
        // 2026-06 league audit — resolve the header-selected league's team,
        // never blind teams[0] (drafting here mutated the wrong league).
        const list = teamRes.data.teams;
        const mine = (passedTeam?.id && list.find(t => t.id === passedTeam.id))
          || (selectedLeagueId && list.find(t => t.league_id === selectedLeagueId))
          || ((!passedTeam?.id && !selectedLeagueId) ? list[0] : null)
          || null;
        setMyTeam(mine);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
    // selectedLeagueId/passedTeam are league-scoping relevant: sub-view
    // frames stay mounted in the nav stack, so re-resolve on change.
  }, [fetchPage, selectedLeagueId, passedTeam?.id]);

  const loadMore = useCallback(async (targetTab) => {
    if (loadingMore[targetTab] || !hasMore[targetTab]) return;
    setLoadingMore(prev => ({ ...prev, [targetTab]: true }));
    try {
      const skip = lists[targetTab].length;
      const market = await fetchPage(skip);
      const newRows = market[targetTab] || [];
      // De-dupe by id in case the backend's sort drifted between pages
      // (e.g. an entity's weekly_points changed between page 1 and 2).
      setLists(prev => {
        const seen = new Set(prev[targetTab].map(e => e.id));
        const merged = [...prev[targetTab], ...newRows.filter(e => !seen.has(e.id))];
        return { ...prev, [targetTab]: merged };
      });
      setHasMore(prev => ({
        ...prev,
        [targetTab]: (market.has_more?.[targetTab] ?? false) && newRows.length > 0,
      }));
    } catch (e) {
      toast.error('Failed to load more — try again in a moment.');
    } finally {
      setLoadingMore(prev => ({ ...prev, [targetTab]: false }));
    }
  }, [fetchPage, lists, loadingMore, hasMore]);

  const handleDraft = async (entity) => {
    if (!myTeam) { toast.error('No team found'); return; }
    try {
      await teamAPI.draft({
        team_id: myTeam.id,
        entity_id: entity.id,
        // Build 4 — cross-league guard: server 409s on stale-league drift.
        league_id: selectedLeagueId || myTeam.league_id,
      });
      toast.success(`Drafted ${entity.name}!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Draft failed');
    }
  };

  const isOnTeam = (id) => myTeam?.slots?.some(s => s.entity_id === id);

  if (loading) {
    return <div className="p-4 space-y-3">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>;
  }

  const sections = {
    top_scorers: { label: 'Top Scorers', icon: Flame },
    most_owned: { label: 'Most Owned', icon: Users },
    rising_stars: { label: 'Rising Stars', icon: Star },
  };

  const activeRows = lists[tab] || [];

  return (
    <div className="p-4" data-testid="transfer-market">
      <div className="flex items-center justify-between mb-4">
        {onBack && <BackButton onClick={onBack} />}
        <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
          <BarChart3 className="h-5 w-5 inline mr-2" />
          Transfer Market
        </h2>
        <div />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full mb-4">
          {Object.entries(sections).map(([key, { label, icon: Icon }]) => (
            <TabsTrigger key={key} value={key} className="flex-1 text-xs gap-1" data-testid={`transfer-market-tab-${key}`}>
              <Icon className="h-3 w-3" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <ScrollArea className="h-[calc(100vh-280px)]">
        <div className="space-y-2" data-testid={`transfer-market-list-${tab}`}>
          {activeRows.map((entity, i) => (
            <div
              key={entity.id}
              className="relative rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => onEntityClick && onEntityClick(entity.id)}
              onKeyDown={onEntityClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEntityClick(entity.id); } } : undefined}
              role={onEntityClick ? 'button' : undefined}
              tabIndex={onEntityClick ? 0 : undefined}
              aria-label={onEntityClick ? `View ${entity.name} details` : undefined}
            >
              <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-secondary flex items-center justify-center text-[9px] font-bold text-muted-foreground">
                {i + 1}
              </div>
              <div className="ml-6">
                <EntityRow
                  entity={entity}
                  onDraft={handleDraft}
                  isDrafted={isOnTeam(entity.id)}
                  showOwnership={tab === 'most_owned'}
                  locked={!!myTeam?.finalized}
                />
              </div>
            </div>
          ))}

          {activeRows.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">No data yet</p>
          )}

          {/* v5.8.4 — Load More gate. Disappears automatically when the
              backend reports `has_more[tab] = false`. */}
          {activeRows.length > 0 && hasMore[tab] && (
            <div className="pt-3 pb-1 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => loadMore(tab)}
                disabled={loadingMore[tab]}
                data-testid={`transfer-market-load-more-${tab}`}
              >
                {loadingMore[tab] ? (
                  <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Loading…</>
                ) : (
                  <>Load more · {activeRows.length} shown</>
                )}
              </Button>
            </div>
          )}

          {activeRows.length > 0 && !hasMore[tab] && (
            <p className="text-center text-[10px] text-muted-foreground py-3" data-testid={`transfer-market-end-${tab}`}>
              All {activeRows.length} {sections[tab].label.toLowerCase()} loaded.
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
